import React, { useState, useEffect, useMemo } from 'react';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { utils, writeFile } from 'xlsx';
import MainLayout from '../../layout/main-layout';
import { APP_NAME } from '../../utils';
import { useDataContext } from '../../context/DataContext';
import { formatDate } from '../../utils/functions';
import Add from './add';
import Update from './update';
import Delete from './delete';
import { useSelector } from 'react-redux';

const SortIcon = ({ active, direction }) => (
  <span className="ms-1 opacity-75">{active ? (direction === 'asc' ? '↑' : '↓') : '↕'}</span>
);

const subCategorySlug = (nom) => (nom || '').replace(/\s/g, '_').toLowerCase();

const statusBadgeClass = (item) =>
  Number(item.private) === 0 ? 'duo-badge-pill duo-badge-pill--ok' : 'duo-badge-pill duo-badge-pill--off';

const statusLabel = (item) => (Number(item.private) === 0 ? 'Active' : 'Inactive');

const getSortValue = (item, key) => {
  if (key === 'createur') return item.createur_categorie?.name ?? '';
  if (key === 'slug') return subCategorySlug(item.nom);
  if (key === 'parent') return item.type_categorie?.nom ?? '';
  return item[key] ?? '';
};

export default function SubCategories() {
  const { categories, fetchCategories } = useDataContext();
  const user = useSelector((state) => state.auth.user);
  const isRole13 = Number(user?.personnels?.[0]?.poste_id) === 13;

  const [searchInput, setSearchInput] = useState('');
  const [appliedFilters, setAppliedFilters] = useState({ search: '', date: '', status: '' });
  const [draftFilters, setDraftFilters] = useState({ date: '', status: '' });

  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: 'nom', direction: 'asc' });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deletingItem, setDeletingItem] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const subCategoriesData = useMemo(
    () => (categories || []).filter((c) => c.type_categorie !== null),
    [categories]
  );

  useEffect(() => {
    if (categories?.length) setIsLoading(false);
  }, [categories]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAppliedFilters((prev) => ({ ...prev, search: searchInput }));
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const filteredItems = useMemo(() => {
    return subCategoriesData.filter((item) => {
      const search = appliedFilters.search.trim().toLowerCase();
      if (search && !item.nom?.toLowerCase().includes(search)) {
        return false;
      }
      if (appliedFilters.status !== '' && Number(item.private) !== Number(appliedFilters.status)) {
        return false;
      }
      if (appliedFilters.date) {
        const itemDate = item.date_creation || item.created_at;
        if (!itemDate || new Date(itemDate).toDateString() !== new Date(appliedFilters.date).toDateString()) {
          return false;
        }
      }
      return true;
    });
  }, [subCategoriesData, appliedFilters]);

  const sortedItems = useMemo(() => {
    const sorted = [...filteredItems];
    const { key, direction } = sortConfig;
    const dir = direction === 'asc' ? 1 : -1;
    sorted.sort((a, b) => {
      const aValue = getSortValue(a, key);
      const bValue = getSortValue(b, key);
      if (aValue < bValue) return -1 * dir;
      if (aValue > bValue) return 1 * dir;
      return 0;
    });
    return sorted;
  }, [filteredItems, sortConfig]);

  const stats = useMemo(
    () => ({
      total: filteredItems.length,
      active: filteredItems.filter((c) => Number(c.private) === 0).length,
      inactive: filteredItems.filter((c) => Number(c.private) !== 0).length,
    }),
    [filteredItems]
  );

  const totalPages = Math.max(1, Math.ceil(sortedItems.length / perPage));
  const indexOfLast = currentPage * perPage;
  const indexOfFirst = indexOfLast - perPage;
  const currentItems = sortedItems.slice(indexOfFirst, indexOfLast);

  const handlePageChange = (direction) => {
    if (direction === 'next' && currentPage < totalPages) setCurrentPage((p) => p + 1);
    else if (direction === 'prev' && currentPage > 1) setCurrentPage((p) => p - 1);
  };

  const requestSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
    setCurrentPage(1);
  };

  const isSortActive = (key) => sortConfig.key === key;

  const handleApplyFilters = (e) => {
    e.preventDefault();
    setAppliedFilters((prev) => ({
      ...prev,
      date: draftFilters.date,
      status: draftFilters.status,
    }));
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setSearchInput('');
    setDraftFilters({ date: '', status: '' });
    setAppliedFilters({ search: '', date: '', status: '' });
    setCurrentPage(1);
    setSortConfig({ key: 'nom', direction: 'asc' });
  };

  const handleRefresh = async () => {
    setIsLoading(true);
    await fetchCategories();
    setIsLoading(false);
    if (currentPage !== 1) setCurrentPage(1);
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.text('Sous-catégories', 14, 16);
    const tableRows = currentItems.map((item) => [
      item.nom,
      subCategorySlug(item.nom),
      item.type_categorie?.nom ?? '—',
      formatDate(item.date_creation || item.created_at),
      item.createur_categorie?.name ?? '—',
      statusLabel(item),
    ]);
    doc.autoTable({
      head: [['Sous-catégorie', 'Slug', 'Catégorie parente', 'Créée le', 'Créé par', 'Statut']],
      body: tableRows,
      startY: 24,
    });
    doc.save(`Sous-catégories ${APP_NAME}.pdf`);
  };

  const exportToExcel = () => {
    const worksheet = utils.json_to_sheet(
      currentItems.map((item) => ({
        'Sous-catégorie': item.nom,
        Slug: subCategorySlug(item.nom),
        'Catégorie parente': item.type_categorie?.nom,
        'Créée le': formatDate(item.date_creation || item.created_at),
        'Créé par': item.createur_categorie?.name,
        Statut: statusLabel(item),
      }))
    );
    const workbook = utils.book_new();
    utils.book_append_sheet(workbook, worksheet, 'Sous-catégories');
    writeFile(workbook, `Sous-catégories ${APP_NAME}.xlsx`);
  };

  const colSpan = isRole13 ? 6 : 7;

  return (
    <MainLayout loading={isLoading}>
      <div className="duo-page">
        <div className="duo-hero duo-hero--compact mb-3">
          <p className="duo-hero-sub mb-1">Catalogue</p>
          <h1 className="duo-hero-title">Sous-catégories</h1>
          <p className="duo-hero-sub mt-2 mb-0">Gérez les sous-catégories liées à vos catégories produits.</p>
        </div>

        <div className="duo-kpi-row">
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">Total (filtre)</p>
            <p className="duo-kpi-chip__value">{stats.total}</p>
          </div>
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">Actives</p>
            <p className="duo-kpi-chip__value">{stats.active}</p>
          </div>
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">Inactives</p>
            <p className="duo-kpi-chip__value">{stats.inactive}</p>
          </div>
        </div>

        <section className="duo-panel">
          <div className="duo-toolbar">
            <span className="fw-bold" style={{ color: '#3c3c3c', fontSize: '0.9rem' }}>
              Liste des sous-catégories
            </span>
            <div className="duo-toolbar__actions">
              <button type="button" title="PDF" onClick={exportToPDF} className="duo-btn duo-btn--ghost duo-btn--icon">
                <i className="bi bi-filetype-pdf" />
              </button>
              <button type="button" title="Excel" onClick={exportToExcel} className="duo-btn duo-btn--ghost duo-btn--icon">
                <i className="bi bi-filetype-xls" />
              </button>
              <button type="button" title="Imprimer" onClick={() => window.print()} className="duo-btn duo-btn--ghost duo-btn--icon">
                <i className="bi bi-printer" />
              </button>
              <button type="button" title="Rafraîchir" onClick={handleRefresh} className="duo-btn duo-btn--ghost duo-btn--icon">
                <i className="bi bi-arrow-clockwise" />
              </button>
              <button
                type="button"
                title="Filtres"
                onClick={() => setFiltersOpen(!filtersOpen)}
                className={`duo-btn duo-btn--ghost duo-btn--icon duo-btn--filter${filtersOpen ? ' is-open' : ''}`}
              >
                <i className={`bi ${filtersOpen ? 'bi-x-lg' : 'bi-funnel-fill'}`} />
              </button>
              {!isRole13 && (
                <button type="button" className="duo-btn duo-btn--primary" onClick={() => setShowAddModal(true)}>
                  <i className="bi bi-plus" /> Nouveau
                </button>
              )}
            </div>
          </div>

          <div className="px-3 pt-2 pb-2 border-bottom" style={{ borderColor: '#f0f0f0' }}>
            <input
              type="search"
              className="duo-input"
              placeholder="Rechercher une sous-catégorie..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>

          {filtersOpen && (
            <div className="duo-filters">
              <form onSubmit={handleApplyFilters}>
                <div className="duo-filters__grid">
                  <input
                    type="date"
                    className="duo-input"
                    title="Date de création"
                    value={draftFilters.date}
                    onChange={(e) => setDraftFilters((prev) => ({ ...prev, date: e.target.value }))}
                  />
                  <select
                    className="duo-select"
                    value={draftFilters.status}
                    onChange={(e) => setDraftFilters((prev) => ({ ...prev, status: e.target.value }))}
                  >
                    <option value="">Tous les statuts</option>
                    <option value="0">Active</option>
                    <option value="1">Inactive</option>
                  </select>
                </div>
                <div className="d-flex justify-content-end gap-2">
                  <button type="button" className="duo-btn duo-btn--ghost" onClick={handleResetFilters}>
                    Réinitialiser
                  </button>
                  <button type="submit" className="duo-btn duo-btn--primary">
                    <i className="bi bi-search" /> Appliquer les filtres
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="duo-table-wrap">
            <table className="duo-table">
              <thead>
                <tr>
                  <th onClick={() => requestSort('nom')}>
                    Sous-catégorie <SortIcon active={isSortActive('nom')} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('slug')}>
                    Slug <SortIcon active={isSortActive('slug')} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('parent')}>
                    Catégorie parente <SortIcon active={isSortActive('parent')} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('date_creation')}>
                    Créée le <SortIcon active={isSortActive('date_creation')} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('createur')}>
                    Créé par <SortIcon active={isSortActive('createur')} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('private')}>
                    Statut <SortIcon active={isSortActive('private')} direction={sortConfig.direction} />
                  </th>
                  {!isRole13 && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {currentItems.length > 0 ? (
                  currentItems.map((item) => (
                    <tr key={item.id}>
                      <td className="fw-bold">{item.nom}</td>
                      <td>{subCategorySlug(item.nom)}</td>
                      <td>{item.type_categorie?.nom ?? '—'}</td>
                      <td className="text-nowrap">{formatDate(item.date_creation || item.created_at)}</td>
                      <td>{item.createur_categorie?.name ?? '—'}</td>
                      <td>
                        <span className={statusBadgeClass(item)}>{statusLabel(item)}</span>
                      </td>
                      {!isRole13 && (
                        <td>
                          <div className="duo-table__actions">
                            <button
                              type="button"
                              title="Modifier"
                              className="duo-btn duo-btn--ghost duo-btn--sm"
                              onClick={() => setEditingItem(item)}
                            >
                              <i className="bi bi-pencil-square" />
                            </button>
                            <button
                              type="button"
                              title="Supprimer"
                              className="duo-btn duo-btn--ghost duo-btn--sm"
                              onClick={() => setDeletingItem(item)}
                            >
                              <i className="bi bi-trash" style={{ color: '#ff4b4b' }} />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={colSpan} className="duo-empty-row">
                      Aucune sous-catégorie correspondant aux critères.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {currentItems.length > 0 && (
            <div className="duo-footer-bar">
              <span>
                {indexOfFirst + 1} – {Math.min(indexOfLast, sortedItems.length)} sur {sortedItems.length}{' '}
                entrée{sortedItems.length > 1 ? 's' : ''}
              </span>
              <ul className="duo-pagination">
                <li>
                  <button
                    type="button"
                    className="duo-btn duo-btn--ghost"
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange('prev')}
                  >
                    Précédent
                  </button>
                </li>
                <li>
                  <span className="duo-pagination__info px-2">
                    Page {currentPage} / {totalPages}
                  </span>
                </li>
                <li>
                  <button
                    type="button"
                    className="duo-btn duo-btn--primary"
                    disabled={currentPage >= totalPages}
                    onClick={() => handlePageChange('next')}
                  >
                    Suivant
                  </button>
                </li>
              </ul>
            </div>
          )}
        </section>
      </div>

      <Add
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        categories={categories}
        refreshData={handleRefresh}
      />
      <Update
        open={!!editingItem}
        onClose={() => setEditingItem(null)}
        categories={categories}
        category={editingItem}
        refreshData={handleRefresh}
      />
      <Delete
        open={!!deletingItem}
        onClose={() => setDeletingItem(null)}
        category={deletingItem}
        refreshData={handleRefresh}
      />
    </MainLayout>
  );
}
